/**
 * React Hook for Legacy Jobs Collection
 *
 * Replicates the critical jobs listener logic from the 18,000-line index.html
 *
 * LEGACY BEHAVIORS REPLICATED:
 * - Filters out deleted jobs (deleted: true)
 * - Filters out local ID jobs (IDs starting with 'local_')
 * - Geocodes addresses with default Newcastle coordinates
 * - Real-time sync with Firestore
 * - Graceful error handling
 *
 * CRITICAL FIELDS PRESERVED:
 * - provisioningItems (Zapier integration)
 * - provisioningStatus (Zapier integration)
 * - gateCode (hidden but critical)
 * - techNotes (hidden but critical)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase';
import { Job } from '../types/schema';
import { useTenantId, tenantIdReadUnion } from './useTenantId';
import { JOB_FALLBACK_COORDS } from '../data/map-config';
import { getCachedJobs, cacheJobsForTech } from './useOfflineCache';

// Default coordinates — the tenant's office (currently the platform default).
// Used as a geocode fallback for jobs missing lat/lng.
// TODO(saas-readiness): JOB_FALLBACK_COORDS is the platform default, not
// tenant.business.officeLocation. Routing through getJobFallbackCoords(tenant)
// is behaviour-sensitive: useMapJobs.isAtFallback compares stored coords for
// EXACT equality against this value to detect ungeocoded jobs, so the write
// and the sentinel must move together. Decouple the "is geocoded" check from
// the fallback coord in a dedicated task before tenant-routing this.
const DEFAULT_LAT = JOB_FALLBACK_COORDS.lat;
const DEFAULT_LNG = JOB_FALLBACK_COORDS.lng;

/**
 * Geocodes a job's address with default coordinates if missing
 * Replicates legacy behavior from line 4569-4587 in index.html
 */
function geocodeJob(job: Job): Job {
  // If coordinates are already present, return as-is
  if (job.lat && job.lng) {
    return job;
  }

  // Apply default Newcastle coordinates
  return {
    ...job,
    lat: job.lat || DEFAULT_LAT,
    lng: job.lng || DEFAULT_LNG,
  };
}

/**
 * Filters out jobs that should not be displayed
 * Replicates legacy filtering logic from line 4481+ in index.html
 */
function shouldIncludeJob(job: Job): boolean {
  // Filter out soft-deleted jobs
  if (job.deleted) {
    return false;
  }

  // Filter out jobs with local IDs (not yet migrated to Firestore)
  if (job.id?.startsWith('local_')) {
    return false;
  }

  // Filter out jobs marked as local
  if (job.isLocalId) {
    return false;
  }

  return true;
}

/**
 * Sorts jobs by date and time (ascending - earliest first)
 * Replicates legacy sorting behavior
 */
function sortJobs(jobs: Job[]): Job[] {
  return [...jobs].sort((a, b) => {
    // First, sort by date
    const dateA = new Date(`${a.date}T${a.time || '00:00'}`);
    const dateB = new Date(`${b.date}T${b.time || '00:00'}`);

    const dateDiff = dateA.getTime() - dateB.getTime();
    if (dateDiff !== 0) {
      return dateDiff;
    }

    // If dates are equal, sort by time
    const timeA = a.time || '00:00';
    const timeB = b.time || '00:00';
    return timeA.localeCompare(timeB);
  });
}

/**
 * Hook to get all jobs with real-time updates
 *
 * Returns:
 * - jobs: Array of Job objects (filtered, geocoded, sorted)
 * - loading: Boolean indicating initial load state
 * - error: Error object if connection/query fails, null otherwise
 *
 * Usage:
 * ```tsx
 * const { jobs, loading, error } = useJobs();
 *
 * if (loading) return <div>Loading jobs...</div>;
 * if (error) return <div>Error: {error.message}</div>;
 *
 * return <JobsList jobs={jobs} />;
 * ```
 */
export function useJobs() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const tenantId = useTenantId();

  // Heartbeat: reconnect Firestore listeners when app returns from background
  // AFTER A LONG IDLE. The previous 5-second threshold meant every tab-switch
  // tore down + recreated BOTH listeners, re-reading the full jobs window
  // from the server each time — that was the dominant source of Firestore
  // read cost (millions of reads/day). Firestore's WebSocket auto-reconnects
  // on its own for short backgrounding; we only need to force a re-subscribe
  // when the connection has been dead long enough to genuinely risk staleness.
  const [refreshKey, setRefreshKey] = useState(0);
  const lastVisibleRef = useRef(Date.now());

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const elapsed = Date.now() - lastVisibleRef.current;
        if (elapsed > 15 * 60_000) {
          setRefreshKey((k) => k + 1);
        }
      }
      lastVisibleRef.current = Date.now();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Track both query results and merge them
  const datedJobsRef = useRef<Job[]>([]);
  const undatedJobsRef = useRef<Job[]>([]);
  const loadedRef = useRef({ dated: false, undated: false });

  useEffect(() => {
    // Reset load tracking
    loadedRef.current = { dated: false, undated: false };

    const mergeAndSet = () => {
      const seen = new Set<string>();
      const merged: Job[] = [];
      for (const job of [...datedJobsRef.current, ...undatedJobsRef.current]) {
        if (job.id && !seen.has(job.id)) {
          seen.add(job.id);
          merged.push(job);
        }
      }
      const filtered = merged.filter(shouldIncludeJob);
      const geocoded = filtered.map(geocodeJob);
      const sorted = sortJobs(geocoded);
      setJobs(sorted);
      if (loadedRef.current.dated && loadedRef.current.undated) {
        setLoading(false);
      }
      setError(null);
    };

    // Query 1: Recent + upcoming jobs (last 90 days + future) by date
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - 90);
    const cutoff = `${cutoffDate.getFullYear()}-${String(cutoffDate.getMonth() + 1).padStart(2, '0')}-${String(cutoffDate.getDate()).padStart(2, '0')}`;

    const datedQuery = query(
      collection(db, 'jobs'),
      // Required for strict tenant-isolation rules. Without this filter,
      // Firestore rejects the listen ("rules are not filters" semantics).
      where('tenantId', 'in', tenantIdReadUnion(tenantId)),
      where('date', '>=', cutoff),
    );

    // Query 2: Unscheduled jobs with empty date (created from deals, not yet scheduled)
    const undatedQuery = query(
      collection(db, 'jobs'),
      where('tenantId', 'in', tenantIdReadUnion(tenantId)),
      where('date', '==', ''),
    );

    const unsubDated = onSnapshot(
      datedQuery,
      (snapshot) => {
        datedJobsRef.current = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() }) as Job
        );
        loadedRef.current.dated = true;
        mergeAndSet();
      },
      (err) => {
        console.error('[useJobs] Dated listener error:', err.message);
        setError(err as Error);
        loadedRef.current.dated = true;
        if (loadedRef.current.undated) setLoading(false);
      }
    );

    const unsubUndated = onSnapshot(
      undatedQuery,
      (snapshot) => {
        undatedJobsRef.current = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() }) as Job
        );
        loadedRef.current.undated = true;
        mergeAndSet();
      },
      (err) => {
        console.error('[useJobs] Undated listener error:', err.message);
        setError(err as Error);
        loadedRef.current.undated = true;
        if (loadedRef.current.dated) setLoading(false);
      }
    );

    return () => {
      unsubDated();
      unsubUndated();
    };
  }, [tenantId, refreshKey]); // Re-run when tenant changes or app returns from background

  return { jobs, loading, error };
}

/**
 * Hook to get jobs filtered by status
 *
 * Usage:
 * ```tsx
 * const { jobs, loading, error } = useJobsByStatus('Scheduled');
 * ```
 */
export function useJobsByStatus(status: string) {
  const { jobs, loading, error } = useJobs();

  const filteredJobs = jobs.filter((job) => job.status === status);

  return { jobs: filteredJobs, loading, error };
}

/**
 * Hook to get jobs assigned to a specific tech
 *
 * OPTIMIZED VERSION:
 * - Queries Firestore directly with where clauses instead of fetching all jobs
 * - Handles both techId (single) and techIds (array) fields
 * - Merges results from both queries and deduplicates
 * - Much faster for large job collections
 *
 * Usage:
 * ```tsx
 * const { jobs, loading, error } = useJobsByTech('tech-123');
 * ```
 */
export function useJobsByTech(techId: string) {
  // Rehydrate from localStorage on mount so techs NEVER see a blank
  // "Nothing scheduled" state while Firestore is reconnecting on a
  // slow network. Cache is written back every time the live listener
  // settles.
  const [jobs, setJobs] = useState<Job[]>(() => (techId ? getCachedJobs(techId) : []));
  const [loading, setLoading] = useState(() => (techId ? getCachedJobs(techId).length === 0 : true));
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // `hasServerData` is distinct from `loading`. `loading` is "no jobs
  // to show yet" (false the moment cache hydrates). `hasServerData`
  // is "Firestore has confirmed a snapshot server-side at least once"
  // — flips true when BOTH onSnapshot listeners report
  // `metadata.fromCache === false`. The PWA splash gates on this so
  // techs don't see stale cached jobs flash before fresh data lands.
  // Starts true when techId is empty so consumers without a logged-in
  // tech don't get stuck waiting forever.
  const [hasServerData, setHasServerData] = useState(() => !techId);
  const tenantId = useTenantId();
  // Ref to track the current techId and prevent stale closure updates
  const currentTechIdRef = useRef(techId);
  // Mirror jobs into a ref so mergeJobs can read the latest value without
  // re-subscribing every render (avoids stale-closure empty wipes).
  const jobsRef = useRef<Job[]>(jobs);
  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  // Heartbeat: force listener reconnect when the app returns from background
  // AFTER A LONG IDLE. Mobile browsers (especially PWA / phone locked) can
  // suspend WebSocket connections, causing Firestore onSnapshot to go stale.
  // Originally fired after 5 seconds of background — but every tab-switch
  // for a notification glance was tearing down BOTH listeners and re-reading
  // the full tech-jobs window from the server, dominating Firestore read
  // cost. Bumped to 15 minutes: the Firestore SDK handles short reconnects
  // on its own; we only force a full re-subscribe when the connection has
  // been dead long enough to risk real staleness. Pull-to-refresh +
  // 'tech-force-refresh' still give techs an immediate manual escape hatch.
  const [refreshKey, setRefreshKey] = useState(0);
  const lastVisibleRef = useRef(Date.now());

  // Manual refresh — tear down + rebuild listeners. Callers can pull-to-refresh
  // to force a fresh server read if Firestore feels stuck / stale.
  const refresh = useCallback(() => {
    setIsRefreshing(true);
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const elapsed = Date.now() - lastVisibleRef.current;
        // Reconnect only after a genuinely long idle (15min) — see comment
        // above for the cost rationale.
        if (elapsed > 15 * 60_000) {
          setRefreshKey((k) => k + 1);
        }
      }
      lastVisibleRef.current = Date.now();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Listen for a global refresh event so one pull-to-refresh at the layout
  // level can ripple through every useJobsByTech instance in the tree.
  useEffect(() => {
    const handler = () => {
      setIsRefreshing(true);
      setRefreshKey((k) => k + 1);
    };
    window.addEventListener('tech-force-refresh', handler);
    return () => window.removeEventListener('tech-force-refresh', handler);
  }, []);

  useEffect(() => {
    currentTechIdRef.current = techId;

    if (!techId) {
      setJobs([]);
      setLoading(false);
      setHasServerData(true);
      return;
    }

    // We need two queries because jobs can have either:
    // 1. techId (single tech) - use where('techId', '==', techId)
    // 2. techIds (array of techs) - use where('techIds', 'array-contains', techId)

    const q1 = query(
      collection(db, 'jobs'),
      where('tenantId', 'in', tenantIdReadUnion(tenantId)),
      where('techId', '==', techId)
    );

    const q2 = query(
      collection(db, 'jobs'),
      where('tenantId', 'in', tenantIdReadUnion(tenantId)),
      where('techIds', 'array-contains', techId)
    );

    // Track jobs from both queries using refs to avoid stale closure issues
    let jobsFromQ1: Job[] = [];
    let jobsFromQ2: Job[] = [];
    let q1Ready = false;
    let q2Ready = false;
    // Track whether each query's latest snapshot is cache-only (no server
    // confirmation yet). An empty cache snapshot shouldn't wipe a visible
    // list — we wait for the server to confirm "really empty".
    let q1FromCache = true;
    let q2FromCache = true;
    // Capture techId at effect creation time to detect stale updates
    const effectTechId = techId;

    // Helper to merge and deduplicate jobs
    const mergeJobs = () => {
      if (!q1Ready || !q2Ready) return;

      // Guard against stale updates from previous listeners during cleanup race
      if (currentTechIdRef.current !== effectTechId) return;

      const allJobs = [...jobsFromQ1, ...jobsFromQ2];

      // Deduplicate by job ID
      const uniqueJobsMap = new Map<string, Job>();
      allJobs.forEach((job) => {
        if (job.id) {
          uniqueJobsMap.set(job.id, job);
        }
      });

      const uniqueJobs = Array.from(uniqueJobsMap.values());

      // Apply legacy filtering logic
      const filteredJobs = uniqueJobs.filter(shouldIncludeJob);

      // Apply geocoding with default coordinates
      const geocodedJobs = filteredJobs.map(geocodeJob);

      // Sort jobs by date/time
      const sortedJobs = sortJobs(geocodedJobs);

      // PROTECT AGAINST PHANTOM SHRINKAGE:
      // On slow networks Firestore can fire listeners with stale cache-only
      // snapshots — sometimes containing just the doc the tech is actively
      // writing to (the current job, kept warm by state transitions). Don't
      // shrink a populated visible list unless BOTH queries are
      // server-confirmed. Previously this only kicked in when BOTH were
      // from cache — but the flicker techs were seeing on refresh
      // (jobs → empty → jobs) came from the mixed state where ONE query
      // had a fresh server-confirmed empty result while the OTHER was
      // still on stale cache. With this stricter gate we accept the
      // emptying only once Firestore has confirmed both sides server-side,
      // which is also the only correct moment to trust a removal.
      const eitherFromCache = q1FromCache || q2FromCache;
      if (
        eitherFromCache &&
        jobsRef.current.length > 0 &&
        sortedJobs.length < jobsRef.current.length
      ) {
        setLoading(false);
        setIsRefreshing(false);
        setError(null);
        return;
      }

      setJobs(sortedJobs);
      jobsRef.current = sortedJobs;
      // Only persist server-confirmed lists. A cache-only snapshot may be
      // a partial slice (e.g. just the active job) — never let it overwrite
      // the trusted localStorage cache that techs fall back to offline.
      if (sortedJobs.length > 0 && !eitherFromCache) {
        cacheJobsForTech(effectTechId, sortedJobs);
      }
      // First server-confirmed snapshot — gate the PWA splash off.
      if (!eitherFromCache) {
        setHasServerData(true);
      }
      setLoading(false);
      setIsRefreshing(false);
      setError(null);
    };

    // Listener for techId field (single tech)
    const unsubscribe1 = onSnapshot(
      q1,
      { includeMetadataChanges: true },
      (snapshot) => {
        jobsFromQ1 = snapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            }) as Job
        );
        q1FromCache = snapshot.metadata?.fromCache ?? false;
        q1Ready = true;
        mergeJobs();
      },
      (err) => {
        console.error(`[useJobsByTech] Error in techId query for tech '${techId}':`, err.message);
        setError(err as Error);
        setHasServerData(true); // unblock splash so the error surfaces
        q1Ready = true;
        mergeJobs();
      }
    );

    // Listener for techIds field (array of techs)
    const unsubscribe2 = onSnapshot(
      q2,
      { includeMetadataChanges: true },
      (snapshot) => {
        jobsFromQ2 = snapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            }) as Job
        );
        q2FromCache = snapshot.metadata?.fromCache ?? false;
        q2Ready = true;
        mergeJobs();
      },
      (err) => {
        console.error(`[useJobsByTech] Error in techIds query for tech '${techId}':`, err.message);
        setError(err as Error);
        setHasServerData(true); // unblock splash so the error surfaces
        q2Ready = true;
        mergeJobs();
      }
    );

    // Safety timeout — if listeners never fire (flaky network / offline),
    // release the refresh indicator after 10s so the UI isn't stuck spinning.
    // Only schedule this on explicit refreshes (refreshKey > 0) to avoid
    // stomping on `isRefreshing` on the initial mount / visibility reconnect.
    let refreshTimeout: ReturnType<typeof setTimeout> | null = null;
    if (refreshKey > 0) {
      refreshTimeout = setTimeout(() => {
        setIsRefreshing(false);
      }, 10_000);
    }

    return () => {
      if (refreshTimeout) clearTimeout(refreshTimeout);
      unsubscribe1();
      unsubscribe2();
    };
  }, [techId, tenantId, refreshKey]);

  return { jobs, loading, error, isRefreshing, refresh, hasServerData };
}

/**
 * Hook to get jobs for a specific customer
 *
 * Usage:
 * ```tsx
 * const { jobs, loading, error } = useJobsByCustomer('customer-456');
 * ```
 */
export function useJobsByCustomer(customerId: string | null) {
  const { jobs, loading, error } = useJobs();

  if (!customerId) {
    return { jobs: [], loading, error };
  }

  const filteredJobs = jobs.filter((job) => job.customerId === customerId);

  return { jobs: filteredJobs, loading, error };
}

/**
 * Hook to get jobs with SOS alerts
 *
 * Usage:
 * ```tsx
 * const { jobs, loading, error } = useJobsWithSOS();
 * ```
 */
export function useJobsWithSOS() {
  const { jobs, loading, error } = useJobs();

  const sosJobs = jobs.filter((job) => job.sos === true);

  return { jobs: sosJobs, loading, error };
}

/**
 * Hook to get flagged jobs
 *
 * Usage:
 * ```tsx
 * const { jobs, loading, error } = useFlaggedJobs();
 * ```
 */
export function useFlaggedJobs() {
  const { jobs, loading, error } = useJobs();

  const flaggedJobs = jobs.filter((job) => job.flagged === true);

  return { jobs: flaggedJobs, loading, error };
}

/**
 * Hook to get jobs by provisioning status (for Zapier integration)
 *
 * Usage:
 * ```tsx
 * const { jobs, loading, error } = useJobsByProvisioningStatus('picked');
 * ```
 */
export function useJobsByProvisioningStatus(
  status: 'pending' | 'to_order' | 'picked'
) {
  const { jobs, loading, error } = useJobs();

  const filteredJobs = jobs.filter(
    (job) => job.provisioningStatus === status
  );

  return { jobs: filteredJobs, loading, error };
}
